/**
 * The Users screen (§A PR-2) — everyone who can sign in to this account, on
 * the six user-management routes (P19–P24).
 *
 * Structure is the `JobListPage` clone the plan prescribes: filters live in
 * the **URL** (a support thread's "the locked accounts are on page 2" must
 * survive being pasted), a changed filter drops `page` (page 4 of a different
 * result set is a different thing that happens to share a number), and search
 * commits on **form submit** rather than per keystroke — a half-typed email is
 * not a query, and firing one per character turns the list into a strobe.
 *
 * **Write affordances are gated on `PERMISSION.usersWrite` and are absent, not
 * disabled** (the nav rule, criterion 11): to someone who may only read the
 * roster, a greyed "Invite" is a permanent advertisement of a thing they
 * cannot do. The gate is presentation only — the API enforces `users.write`
 * on every one of these routes independently.
 */

import type { UserDto } from '@rd/shared';
import { useCallback, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider.js';
import { PERMISSION } from '../../auth/keys.js';
import { Badge, type BadgeTone } from '../../components/Badge.js';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Field } from '../../components/Field.js';
import { Input, Select } from '../../components/Input.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Pagination } from '../../components/Pagination.js';
import { Skeleton } from '../../components/Skeleton.js';
import { Table, type Column } from '../../components/Table.js';
import { useToast } from '../../components/Toast.js';
import { downloadCsv, toCsv, type CsvColumn } from '../../lib/csv.js';

import { InviteAcceptLink, UserFormModal } from './UserFormModal.js';
import { useForceLogout, useResendInvite, useUsers } from './useUsers.js';

/**
 * The status filter's options — the DTO union from `@rd/shared`'s
 * `UserSummary`, which is also the enum `GET /users` validates against
 * (`users.schema.ts`). Ours, so listing it is transcription, not a guess.
 */
const STATUS_FILTERS: readonly UserDto['status'][] = ['INVITED', 'ACTIVE', 'LOCKED', 'DISABLED'];

/**
 * Status → pill tone. `Record` over the union rather than a partial map: a
 * fifth status added to the contract is a compile error here, not a row
 * rendering with no pill.
 */
const STATUS_TONE: Record<UserDto['status'], BadgeTone> = {
  INVITED: 'progress',
  ACTIVE: 'success',
  LOCKED: 'warning',
  DISABLED: 'neutral',
};

function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

export function UserListPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const toast = useToast();

  const status = params.get('status') ?? '';
  const search = params.get('search') ?? '';
  /** The text box is a draft; only Submit writes it into the URL. */
  const [searchDraft, setSearchDraft] = useState(search);

  const canWrite = hasPermission(PERMISSION.usersWrite);

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value === '') next.delete(key);
      else next.set(key, value);
      // A new filter is a new result set (the JobListPage rule): keeping
      // `page=4` would land on an empty page that looks like "no users".
      next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const query = useUsers({
    ...(status === '' ? {} : { status }),
    ...(search === '' ? {} : { search }),
  });

  const resend = useResendInvite();
  const forceLogout = useForceLogout();

  /** `{ mode: 'create' }`, or the row being edited. `null` = closed. */
  const [modal, setModal] = useState<{ user: UserDto | null } | null>(null);
  /**
   * A resent invite's development token, kept next to the ref it belongs to so
   * the link renders under the header until the next gesture replaces it.
   * Production never returns one, so this panel simply never appears there.
   */
  const [resent, setResent] = useState<{ email: string; token: string } | null>(null);

  const doResend = async (user: UserDto): Promise<void> => {
    try {
      const result = await resend.mutateAsync(user.ref);
      if (result.developmentInviteToken) {
        setResent({ email: user.email, token: result.developmentInviteToken });
      } else {
        setResent(null);
        toast.push('success', `Invite re-sent to ${user.email}.`);
      }
    } catch {
      toast.push('error', 'The invite could not be re-sent.');
    }
  };

  const doForceLogout = async (user: UserDto): Promise<void> => {
    try {
      await forceLogout.mutateAsync(user.ref);
      toast.push('success', `${user.name} is signed out everywhere.`);
    } catch {
      toast.push('error', 'Could not sign them out.');
    }
  };

  const columns: Column<UserDto>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (user) => <span className="font-medium text-body">{user.name}</span>,
    },
    {
      // Monospace, like every identifier column: an operator matching an
      // address against a ticket compares it character by character.
      key: 'email',
      header: 'Email',
      kind: 'mono',
      render: (user) => <span className="text-sm">{user.email}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      kind: 'status',
      render: (user) => (
        <Badge size="sm" tone={STATUS_TONE[user.status]}>
          {user.status}
        </Badge>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (user) => (
        <span className="text-muted">{user.roles.map((r) => r.name).join(', ') || '—'}</span>
      ),
    },
    {
      key: 'lastLoginAt',
      header: 'Last sign-in',
      render: (user) => <span className="text-muted">{formatWhen(user.lastLoginAt)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      kind: 'actions',
      render: (user) => (
        <div className="flex items-center justify-end gap-[var(--space-2)]">
          {canWrite ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setModal({ user })}>
                Edit
              </Button>
              {/* Only a pending invite can be re-sent; on anyone else the
                  route would 409 and the button would be a lie. */}
              {user.status === 'INVITED' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={resend.isPending}
                  onClick={() => void doResend(user)}
                >
                  Resend invite
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                loading={forceLogout.isPending}
                onClick={() => void doForceLogout(user)}
              >
                Force logout
              </Button>
            </>
          ) : null}
          {/* The pivot the audit screen's actorRef filter exists for:
              "everything this person did", as a link that survives pasting. */}
          <Link
            to={`/audit?actorRef=${user.ref}`}
            className="text-sm text-primary underline"
          >
            View audit
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        description="Everyone who can sign in to this account, and what they are allowed to do."
        actions={
          <div className="flex items-center gap-[var(--space-2)]">
            {/* §A PR-6: built from the server-returned rows on screen — any
                masking the API applied exports as sent, by construction. */}
            <Button
              variant="secondary"
              disabled={(query.data?.data.length ?? 0) === 0}
              onClick={() => {
                const rows = query.data?.data ?? [];
                const columns: CsvColumn<UserDto>[] = [
                  { header: 'ref', value: (u) => u.ref },
                  { header: 'email', value: (u) => u.email },
                  { header: 'name', value: (u) => u.name },
                  { header: 'status', value: (u) => u.status },
                  { header: 'roles', value: (u) => u.roles.map((r) => r.name).join('; ') },
                  { header: 'lastLoginAt', value: (u) => u.lastLoginAt },
                  { header: 'createdAt', value: (u) => u.createdAt },
                ];
                downloadCsv(`users-page-${query.page}.csv`, toCsv(columns, rows));
              }}
            >
              Download CSV (this page)
            </Button>
            {canWrite ? (
              <Button onClick={() => setModal({ user: null })}>Invite user</Button>
            ) : null}
          </div>
        }
      />

      <section aria-label="Filters" className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Status">
          {(field) => (
            <Select
              id={field.id}
              value={status}
              onChange={(event) => setFilter('status', event.target.value)}
            >
              <option value="">Any status</option>
              {STATUS_FILTERS.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <form
          className="sm:col-span-1 lg:col-span-2"
          onSubmit={(event) => {
            event.preventDefault();
            setFilter('search', searchDraft.trim());
          }}
        >
          <Field label="Search" hint="Name or email. Press Enter to search.">
            {(field) => (
              <div className="flex items-end gap-[var(--space-2)]">
                <Input
                  id={field.id}
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                />
                <Button type="submit" variant="secondary">
                  Search
                </Button>
              </div>
            )}
          </Field>
        </form>
      </section>

      {resent !== null ? (
        <div className="mb-6" aria-label={`Invite link for ${resent.email}`}>
          <InviteAcceptLink token={resent.token} />
        </div>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : null}

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data ? (
        <>
          <Table
            caption="Users, newest first"
            columns={columns}
            rows={query.data.data}
            rowKey={(user) => user.ref}
            empty={
              <EmptyState
                title="Nobody matches"
                body="Change the filters, or invite someone new."
              />
            }
          />
          <Pagination meta={query.data.meta} onPageChange={query.setPage} label="Users" />
        </>
      ) : null}

      <UserFormModal
        open={modal !== null}
        user={modal?.user ?? null}
        onClose={() => setModal(null)}
      />
    </>
  );
}
